/**
 * Patching a cached inbox page when the viewer marks a conversation read.
 *
 * Calculation only — no hooks, no cache, no fetching — so the one rule that decides what
 * a read does to a list is testable on its own. `useMarkChannelRead` applies it to every
 * cached filter variant of the inbox query.
 *
 * ## Why the list is patched rather than refetched
 *
 * A read is a LOCAL fact about one conversation. It changes `unreadCount` on one row, and
 * membership only on a page that asked for unread conversations: the inbox is ordered by
 * activity, which a read cannot move. So the patch is the whole truth, and the list never
 * has to refetch — which is what stops it reflowing and scrolling under the cursor a few
 * hundred milliseconds after the click.
 *
 * One ordering is the exception and is left alone deliberately: under the display options'
 * "Show unread first" (`sort=-unread,…`) a read DOES move the row, out of the unread block.
 * Patching keeps it where the reader left it until the next refetch — the list is 15s-stale
 * and realtime-invalidated, so it settles on its own rather than jumping under the cursor.
 *
 * ## Why it takes the query key
 *
 * Whether the row should DISAPPEAR or merely lose its badge is a property of the page
 * being patched, not of the write: `filters[unread][eq]=true` is a real server predicate,
 * so a read row does not belong on that page, while on an unfiltered page it belongs
 * exactly where it is. The key is the only place that question is answered, which is why
 * this cannot use the client's built-in `optimistic: { type: "patch" }` — its `apply`
 * never sees the key.
 *
 * ## One rule, however the cursor was advanced
 *
 * A read drops the row from an unread-filtered page whether the reader pressed "Mark as
 * read" or the open thread marked itself read on view after its delay. Dropping it used to
 * be opt-in, on the reasoning that a conversation someone is READING should not be pulled
 * out from under them — but the row it left behind sat on a list whose one question it no
 * longer answered, with an unlit badge, until some later refetch removed it. The thread is
 * addressed by its own cid and loads independently of the list, so the reader loses
 * nothing by the row leaving, and the two ways of clearing a badge now differ in nothing.
 */

/**
 * The minimum a row must carry. Declared structurally rather than imported: the full
 * `InboxConversation` belongs to `features/shared/inbox`, which imports THIS slice, so
 * naming it here would close a cycle.
 */
type ReadableRow = { cid: string; unreadCount: number };

/** A cached inbox page: the `{ data }` envelope the generated client stores. */
type InboxPage<TRow> = { data?: TRow[] } | undefined;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The query object out of an inbox key.
 *
 * The inbox is a list route, so its key is `["channels", "list", "inbox", params]` (plus
 * `"infinite"` for a paged list), the list params carrying `take` and `filters`. Found by
 * shape rather than by index so a change in the key's slots cannot silently shift it and
 * turn this into a wrong answer instead of no answer.
 */
function queryOf(queryKey: readonly unknown[]): Record<string, unknown> | null {
  for (let index = queryKey.length - 1; index >= 0; index--) {
    const part = queryKey[index];
    if (isRecord(part) && "take" in part) return part;
  }
  return null;
}

/**
 * Whether the page this key names asked for unread conversations only.
 *
 * `buildInboxQuery` writes the value as the STRING `"true"` (the canonical wire form),
 * never a boolean — and `"false"` is a real, different question ("conversations I have
 * read"), so it must not count here.
 */
function isUnreadOnlyPage(queryKey: readonly unknown[]): boolean {
  const filters = queryOf(queryKey)?.filters;
  if (!isRecord(filters)) return false;

  const unread = filters.unread;
  if (isRecord(unread)) return unread.eq === "true";
  return unread === "true";
}

/**
 * Returns the page with `cid` marked read, or the page unchanged BY REFERENCE when this
 * page does not hold that conversation — so the filter variants a read does not touch do
 * not re-render.
 */
export function patchInboxPageForRead<TRow extends ReadableRow>(
  page: InboxPage<TRow>,
  queryKey: readonly unknown[],
  cid: string,
): InboxPage<TRow> {
  const rows = page?.data;
  if (!page || !rows) return page;
  if (!rows.some((row) => row.cid === cid)) return page;

  if (isUnreadOnlyPage(queryKey)) {
    return { ...page, data: rows.filter((row) => row.cid !== cid) };
  }

  return {
    ...page,
    // Copy-on-write, and only the row that changed — every other row keeps its identity.
    data: rows.map((row) =>
      row.cid === cid ? { ...row, unreadCount: 0 } : row,
    ),
  };
}

/**
 * A cached inbox list as `.getInfinite()` stores it — one page per offset loaded so far.
 */
type InboxCache<TRow> =
  | { pages: InboxPage<TRow>[]; pageParams: unknown[] }
  | undefined;

/**
 * The same patch, across every page the list has loaded.
 *
 * The list is an infinite query, so the conversation can sit on any offset the reader has
 * scrolled to — a patch that reached for one `data` array would find nothing on a cache
 * shaped `{ pages }` and silently do nothing, which is the failure mode this exists to
 * rule out.
 *
 * Returns the cache unchanged BY REFERENCE when no page held the conversation — the same
 * guarantee `patchInboxPageForRead` gives per page, one level up — so the filter variants
 * a read does not touch do not re-render. The conversation lives on exactly one page, so
 * every other page keeps its identity too and the list re-renders one row.
 */
export function patchInboxCacheForRead<TRow extends ReadableRow>(
  cache: InboxCache<TRow>,
  queryKey: readonly unknown[],
  cid: string,
): InboxCache<TRow> {
  if (!cache?.pages) return cache;

  let changed = false;
  const pages = cache.pages.map((page) => {
    const patched = patchInboxPageForRead(page, queryKey, cid);
    if (patched !== page) changed = true;
    return patched;
  });

  return changed ? { ...cache, pages } : cache;
}
